<template>
  <div class="bg-slate-50">
    <!-- Hero Section -->
    <section class="py-20 px-4">
      <div class="max-w-4xl mx-auto text-center">
        <!-- Badge -->
        <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 bg-white text-gray-800 border border-tile-border">
          <PhLightning class="w-4 h-4 text-neon-green" weight="fill" />
          <span>Get 500 Free Sparks - No Credit Card Required</span>
        </div>

        <!-- Heading -->
        <h2 class="text-5xl font-bold mb-6 text-gray-900">
          Welcome to the
          <span class="text-neon-green">Happy Alien</span>
          Ecosystem
        </h2>

        <p class="text-xl mb-8 text-gray-600">
          Transform your learning experience with our suite of AI-powered tools
        </p>

        <!-- CTA Buttons -->
        <div class="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <button @click="$emit('signup')" class="btn btn-primary btn-lg">
            <span class="flex items-center gap-2">
              Get 500 Free Sparks
              <PhArrowRight class="w-5 h-5" weight="bold" />
            </span>
          </button>
          <button @click="$emit('login')" class="btn btn-neon">
            Sign In
          </button>
        </div>

        <!-- Spark system tagline -->
        <div class="flex items-center justify-center gap-2 text-lg text-gray-600">
          <PhLightning class="w-5 h-5 text-neon-green" weight="fill" />
          <span>Powered by Sparks - Our unified credit system</span>
        </div>
      </div>
    </section>

    <!-- Products Grid -->
    <section class="py-16 px-4">
      <div class="max-w-7xl mx-auto">
        <h3 class="text-3xl font-bold text-center mb-12 text-gray-900">
          Our Products
        </h3>

        <!-- Build Category -->
        <div class="mb-12">
          <h4 class="text-lg font-semibold text-neon-green mb-6 flex items-center gap-2">
            <PhHammer class="w-5 h-5" weight="fill" />
            Build
          </h4>
          <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <!-- VidSabio -->
            <div class="card group">
              <div class="flex items-center gap-3 mb-4">
                <div class="tool-icon">
                  <PhFilmSlate class="w-6 h-6" weight="fill" />
                </div>
                <h4 class="text-xl font-bold text-gray-900">VidSabio</h4>
              </div>
              <p class="mb-4 text-gray-600">
                Transform videos into interactive courses with AI-powered transcription, chapters, and quiz generation. Upload files or paste YouTube URLs.
              </p>
              <ul class="space-y-2 text-sm mb-6 text-gray-500">
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>Automatic chapter detection</span>
                </li>
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>AI-generated quizzes</span>
                </li>
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>SCORM export</span>
                </li>
              </ul>
              <a href="https://vidsabio.happyalien.ai" class="btn btn-neon w-full text-center">
                Visit VidSabio
              </a>
            </div>

            <!-- AI Media Studio -->
            <div class="card group">
              <div class="flex items-center gap-3 mb-4">
                <div class="tool-icon">
                  <PhImage class="w-6 h-6" weight="fill" />
                </div>
                <h4 class="text-xl font-bold text-gray-900">AI Media Studio</h4>
              </div>
              <p class="mb-4 text-gray-600">
                Generate stunning images, videos, voiceovers, and sound effects using cutting-edge AI models for your eLearning projects.
              </p>
              <ul class="space-y-2 text-sm mb-6 text-gray-500">
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>Text-to-image & video</span>
                </li>
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>AI voiceovers</span>
                </li>
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>Character pose packs</span>
                </li>
              </ul>
              <a href="https://media.happyalien.ai" class="btn btn-neon w-full text-center">
                Visit AI Media Studio
              </a>
            </div>

            <!-- Storyboard2Rise -->
            <div class="card group">
              <div class="flex items-center gap-3 mb-4">
                <div class="tool-icon">
                  <PhFileDoc class="w-6 h-6" weight="fill" />
                </div>
                <h4 class="text-xl font-bold text-gray-900">Storyboard2Rise</h4>
              </div>
              <p class="mb-4 text-gray-600">
                Convert Word storyboard documents into fully-built Articulate Rise courses. AI-powered analysis with 25+ Rise block types supported.
              </p>
              <ul class="space-y-2 text-sm mb-6 text-gray-500">
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>Word to Rise conversion</span>
                </li>
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>25+ block types</span>
                </li>
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>Chrome extension automation</span>
                </li>
              </ul>
              <a href="https://storyboard2rise.happyalien.ai" class="btn btn-neon w-full text-center">
                Visit Storyboard2Rise
              </a>
            </div>
          </div>
        </div>

        <!-- Transform Category -->
        <div class="mb-12">
          <h4 class="text-lg font-semibold text-neon-blue mb-6 flex items-center gap-2">
            <PhArrowsClockwise class="w-5 h-5" weight="fill" />
            Transform
          </h4>
          <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <!-- Course Extractor -->
            <div class="card group">
              <div class="flex items-center gap-3 mb-4">
                <div class="tool-icon">
                  <PhExport class="w-6 h-6" weight="fill" />
                </div>
                <h4 class="text-xl font-bold text-gray-900">Course Extractor</h4>
              </div>
              <p class="mb-4 text-gray-600">
                Upload a Storyline, Rise, Captivate, SCORM, or hand-built HTML course and get its text, media, quizzes, and structure back as a clean extraction package — plus prompts to rebuild it in a modern stack.
              </p>
              <ul class="space-y-2 text-sm mb-6 text-gray-500">
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>Storyline, Rise, Captivate, SCORM & HTML</span>
                </li>
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>Content JSON, Markdown & original assets</span>
                </li>
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>AI rebuild prompts included</span>
                </li>
              </ul>
              <a href="https://extractor.happyalien.ai" class="btn btn-neon w-full text-center">
                Visit Course Extractor
              </a>
            </div>

            <!-- Course Translator -->
            <div class="card group">
              <div class="flex items-center gap-3 mb-4">
                <div class="tool-icon">
                  <PhTranslate class="w-6 h-6" weight="fill" />
                </div>
                <h4 class="text-xl font-bold text-gray-900">Course Translator</h4>
              </div>
              <p class="mb-4 text-gray-600">
                Translate Captivate courses into 30+ languages with AI. Creates a single multilingual package with built-in language selector.
              </p>
              <ul class="space-y-2 text-sm mb-6 text-gray-500">
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>30+ languages</span>
                </li>
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>Single multilingual package</span>
                </li>
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>Built-in language selector</span>
                </li>
              </ul>
              <a href="https://course-translator.happyalien.ai" class="btn btn-neon w-full text-center">
                Visit Course Translator
              </a>
            </div>

            <!-- Course Editor -->
            <div class="card group">
              <div class="flex items-center gap-3 mb-4">
                <div class="tool-icon">
                  <PhPencilSimple class="w-6 h-6" weight="fill" />
                </div>
                <h4 class="text-xl font-bold text-gray-900">Course Editor</h4>
              </div>
              <p class="mb-4 text-gray-600">
                Edit published SCORM courses (Captivate, Storyline, Rise) directly in browser. Find & replace, swap images, update audio—no source files needed.
              </p>
              <ul class="space-y-2 text-sm mb-6 text-gray-500">
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>No source files needed</span>
                </li>
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>Find & replace</span>
                </li>
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>Image & audio swap</span>
                </li>
              </ul>
              <a href="https://course-editor.happyalien.ai" class="btn btn-neon w-full text-center">
                Visit Course Editor
              </a>
            </div>
          </div>
        </div>

        <!-- Engage Category -->
        <div class="mb-12">
          <h4 class="text-lg font-semibold text-purple-500 mb-6 flex items-center gap-2">
            <PhBroadcast class="w-5 h-5" weight="fill" />
            Engage
          </h4>
          <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <!-- CourseSignal -->
            <div class="card group">
              <div class="flex items-center gap-3 mb-4">
                <div class="tool-icon">
                  <PhBroadcast class="w-6 h-6" weight="fill" />
                </div>
                <h4 class="text-xl font-bold text-gray-900">CourseSignal</h4>
              </div>
              <p class="mb-4 text-gray-600">
                Inject alerts, polls, feedback forms, and CTAs into any eLearning course without republishing. Smart triggers and real-time analytics.
              </p>
              <ul class="space-y-2 text-sm mb-6 text-gray-500">
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>No republishing needed</span>
                </li>
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>Polls & feedback forms</span>
                </li>
                <li class="flex items-start gap-2">
                  <PhCheckCircle class="w-4 h-4 text-neon-green mt-0.5 flex-shrink-0" weight="fill" />
                  <span>Real-time analytics</span>
                </li>
              </ul>
              <a href="https://coursesignal.happyalien.ai" class="btn btn-neon w-full text-center">
                Visit CourseSignal
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Sparks Section -->
    <section class="py-16 px-4 bg-white">
      <div class="max-w-4xl mx-auto text-center">
        <div class="inline-flex items-center gap-3 mb-6">
          <PhLightning class="w-10 h-10 text-neon-green" weight="fill" />
          <h3 class="text-3xl font-bold text-gray-900">Unified Spark System</h3>
        </div>
        <p class="text-lg mb-8 text-gray-600">
          One credit system across all Happy Alien products. Buy once, use anywhere.
        </p>
        <div class="grid md:grid-cols-3 gap-6">
          <div class="card">
            <div class="tool-icon mx-auto mb-4">
              <PhCurrencyCircleDollar class="w-6 h-6" weight="fill" />
            </div>
            <h4 class="font-semibold mb-2 text-gray-900">Simple Pricing</h4>
            <p class="text-sm text-gray-600">
              Pay as you go or subscribe for monthly spark credits
            </p>
          </div>
          <div class="card">
            <div class="tool-icon mx-auto mb-4">
              <PhArrowsClockwise class="w-6 h-6" weight="fill" />
            </div>
            <h4 class="font-semibold mb-2 text-gray-900">Cross-Product</h4>
            <p class="text-sm text-gray-600">
              Use your sparks across any Happy Alien product
            </p>
          </div>
          <div class="card">
            <div class="tool-icon mx-auto mb-4">
              <PhChartBar class="w-6 h-6" weight="fill" />
            </div>
            <h4 class="font-semibold mb-2 text-gray-900">Track Usage</h4>
            <p class="text-sm text-gray-600">
              Monitor your spark consumption in real-time
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-20 px-4 bg-slate-50 border-t border-tile-border">
      <div class="max-w-4xl mx-auto text-center">
        <h3 class="text-3xl font-bold mb-6 text-gray-900">
          Ready to Transform Your Learning?
        </h3>
        <p class="text-lg mb-8 text-gray-600">
          Join thousands of users already using Happy Alien's AI-powered tools
        </p>
        <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button @click="$emit('signup')" class="btn btn-primary btn-lg">
            Create Free Account
          </button>
          <button @click="$emit('login')" class="btn btn-neon">
            <span class="flex items-center gap-2">
              Sign In
              <PhArrowRight class="w-4 h-4" weight="bold" />
            </span>
          </button>
        </div>
        <p class="text-sm mt-6 text-gray-500">
          Get 500 free Sparks when you sign up - No credit card required
        </p>
      </div>
    </section>

    <!-- Footer -->
    <footer class="py-8 px-4 bg-gray-900">
      <div class="max-w-7xl mx-auto text-center">
        <div class="flex items-center justify-center gap-2 mb-4">
          <img src="/logo.png" alt="Happy Alien" class="w-8 h-8 object-contain" />
          <span class="font-semibold text-white">Happy Alien</span>
        </div>
        <p class="text-sm text-gray-400">2024 Happy Alien. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import {
  PhLightning,
  PhArrowRight,
  PhFilmSlate,
  PhImage,
  PhBroadcast,
  PhCheckCircle,
  PhCurrencyCircleDollar,
  PhArrowsClockwise,
  PhChartBar,
  PhHammer,
  PhFileDoc,
  PhExport,
  PhTranslate,
  PhPencilSimple,
} from "@phosphor-icons/vue";

defineEmits<{
  login: [];
  signup: [];
}>();
</script>
